import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaWhatsapp, FaPhone, FaEnvelope, FaShareAlt, FaRegCopy, FaEdit, FaQrcode } from "react-icons/fa";
import axiosInstance from "../components/api/axiosInstance";
import { SupportSortableHeader } from "../pages/SupportSortableHeader";
import { generateMobileDialQrDataUrl, MobileDialQrModal } from "./MobileDialQr";

function toPascal(value) {
  const s = String(value || "").trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : "";
}

function normalizedCountryCode(l) {
  const raw = String(l.Country_Code || "").trim();
  if (!raw) return "";
  return raw.startsWith("+") ? raw : `+${raw}`;
}

function buildShare(l) {
  const shareName =
    [toPascal(l.First_Name), toPascal(l.Last_Name)].filter(Boolean).join(" ") ||
    `#${l.Real_Estate_Lead_Id}`;
  const cc = normalizedCountryCode(l);
  const title = `Real Estate Lead - ${shareName}`;
  const text = [
    title,
    l.Mobile_Number ? `Contact: ${[cc, l.Mobile_Number].filter(Boolean).join(" ")}` : "",
    l.Email ? `Email: ${l.Email}` : "",
    l.Property_Service ? `Service: ${l.Property_Service}` : "",
    l.Tenant_Project_Name ? `Project: ${l.Tenant_Project_Name}` : "",
    l.Property_Type_Name ? `Property Type: ${l.Property_Type_Name}` : "",
    l.Requirement_Type ? `Requirement Type: ${l.Requirement_Type}` : "",
    l.Preferred_Location ? `Location: ${l.Preferred_Location}` : "",
  ]
    .filter(Boolean)
    .join("\n");
  return { title, text };
}

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

// A real-estate row is classified into Lead / Client / Customer by status
// (see leadClassification.js). Buckets are mutually exclusive.
function fullName(l) {
  return `${(l.First_Name || "").trim()} ${(l.Last_Name || "").trim()}`.trim() || "—";
}

function phone(l) {
  const cc = l.Country_Code ? `${l.Country_Code.startsWith("+") ? "" : "+"}${l.Country_Code} ` : "";
  return l.Mobile_Number ? `${cc}${l.Mobile_Number}` : "—";
}

// Value accessors used for sorting each column.
const SORT_ACCESSORS = {
  name: (l) => fullName(l).toLowerCase(),
  contact: (l) => l.Mobile_Number || "",
  email: (l) => (l.Email || "").toLowerCase(),
  property: (l) => (l.Property_Service || "").toLowerCase(),
  project: (l) => (l.Tenant_Project_Name || "").toLowerCase(),
  requirement: (l) => (l.Requirement_Type || "").toLowerCase(),
  status: (l) => (l.Lead_Status || "").toLowerCase(),
  created: (l) => (l.Created_On ? new Date(l.Created_On).getTime() || 0 : 0),
};

const COLUMNS = [
  { key: "name", label: "Name" },
  { key: "contact", label: "Contact" },
  { key: "email", label: "Email" },
  { key: "property", label: "Service" },
  { key: "project", label: "Project" },
  { key: "requirement", label: "Requirement" },
  { key: "status", label: "Status" },
  { key: "created", label: "Created" },
];

const DATE_PRESETS = [
  { id: "all", label: "All time" },
  { id: "today", label: "Today" },
  { id: "7", label: "Last 7 days" },
  { id: "30", label: "Last 30 days" },
  { id: "month", label: "This month" },
  { id: "custom", label: "Custom" },
];

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}
function endOfDay(d) {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}

function presetRange(preset, fromDate, toDate) {
  const now = new Date();
  switch (preset) {
    case "today":
      return { from: startOfDay(now), to: endOfDay(now) };
    case "7": {
      const from = startOfDay(now);
      from.setDate(from.getDate() - 6);
      return { from, to: endOfDay(now) };
    }
    case "30": {
      const from = startOfDay(now);
      from.setDate(from.getDate() - 29);
      return { from, to: endOfDay(now) };
    }
    case "month":
      return { from: startOfDay(new Date(now.getFullYear(), now.getMonth(), 1)), to: endOfDay(now) };
    case "custom":
      return {
        from: fromDate ? startOfDay(new Date(fromDate)) : null,
        to: toDate ? endOfDay(new Date(toDate)) : null,
      };
    default:
      return { from: null, to: null };
  }
}

function toNumber(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(String(v).replace(/[^\d.-]/g, ""));
  return Number.isNaN(n) ? null : n;
}

function CustomerLeads({ mode = "leads" }) {
  const navigate = useNavigate();
  const heading = mode === "customers" ? "Customers" : mode === "clients" ? "Clients" : "Leads";
  const noun = mode === "customers" ? "customer" : mode === "clients" ? "client" : "lead";
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [datePreset, setDatePreset] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [minBudget, setMinBudget] = useState("");
  const [maxBudget, setMaxBudget] = useState("");
  const [sortKey, setSortKey] = useState("created");
  const [sortDir, setSortDir] = useState("desc");
  const [shareModal, setShareModal] = useState({ open: false, title: "", text: "" });
  const [qrModal, setQrModal] = useState({ open: false, dataUrl: "", title: "", number: "" });

  const openShare = (l) => {
    const { title, text } = buildShare(l);
    setShareModal({ open: true, title, text });
  };
  const closeShare = () => setShareModal({ open: false, title: "", text: "" });

  const openMobileQr = async (lead) => {
    if (!lead?.Mobile_Number) return;
    try {
      const dataUrl = await generateMobileDialQrDataUrl(lead.Country_Code, lead.Mobile_Number);
      if (!dataUrl) return;
      setQrModal({
        open: true,
        dataUrl,
        title: fullName(lead),
        number: phone(lead),
      });
    } catch (err) {
      console.error("Error generating mobile QR code:", err);
      alert("Unable to generate QR code for this number.");
    }
  };
  const closeMobileQr = () => setQrModal({ open: false, dataUrl: "", title: "", number: "" });

  const copyShareText = async () => {
    const text = shareModal.text;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      alert("Lead details copied to clipboard.");
    } catch (_) {
      alert("Unable to copy details.");
    }
  };

  const nativeShare = async () => {
    if (!navigator.share) return;
    try {
      await navigator.share({ title: shareModal.title, text: shareModal.text });
      closeShare();
    } catch (_) {
      /* user cancelled */
    }
  };

  const toggleSort = (key) => {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axiosInstance.get("/client/leads");
        if (!active) return;
        setLeads(Array.isArray(res.data?.data) ? res.data.data : []);
      } catch (err) {
        if (!active) return;
        setError(err.response?.data?.message || "Could not load your leads.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const statusOptions = useMemo(
    () => Array.from(new Set(leads.map((l) => l.Lead_Status).filter(Boolean))).sort(),
    [leads]
  );
  const sourceOptions = useMemo(
    () => Array.from(new Set(leads.map((l) => l.Source_Name).filter(Boolean))).sort(),
    [leads]
  );
  const locationOptions = useMemo(
    () => Array.from(new Set(leads.map((l) => l.Preferred_Location).filter(Boolean))).sort(),
    [leads]
  );

  const filtered = useMemo(() => {
    let list = leads;

    const { from, to } = presetRange(datePreset, fromDate, toDate);
    if (from || to) {
      list = list.filter((l) => {
        if (!l.Created_On) return false;
        const created = new Date(l.Created_On);
        if (Number.isNaN(created.getTime())) return false;
        if (from && created < from) return false;
        if (to && created > to) return false;
        return true;
      });
    }

    if (statusFilter) list = list.filter((l) => l.Lead_Status === statusFilter);
    if (sourceFilter) list = list.filter((l) => l.Source_Name === sourceFilter);
    if (locationFilter) list = list.filter((l) => l.Preferred_Location === locationFilter);

    const min = toNumber(minBudget);
    const max = toNumber(maxBudget);
    if (min !== null) {
      list = list.filter((l) => {
        const v = toNumber(l.Budget_Max) ?? toNumber(l.Budget_Min);
        return v !== null && v >= min;
      });
    }
    if (max !== null) {
      list = list.filter((l) => {
        const v = toNumber(l.Budget_Min) ?? toNumber(l.Budget_Max);
        return v !== null && v <= max;
      });
    }

    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((l) =>
      [l.First_Name, l.Last_Name, l.Mobile_Number, l.Email, l.Property_Service, l.Tenant_Project_Name, l.Preferred_Location]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [
    leads,
    search,
    datePreset,
    fromDate,
    toDate,
    statusFilter,
    sourceFilter,
    locationFilter,
    minBudget,
    maxBudget,
  ]);

  const hasActiveFilters =
    datePreset !== "all" ||
    !!statusFilter ||
    !!sourceFilter ||
    !!locationFilter ||
    !!minBudget ||
    !!maxBudget ||
    !!search;

  const clearFilters = () => {
    setDatePreset("all");
    setFromDate("");
    setToDate("");
    setStatusFilter("");
    setSourceFilter("");
    setLocationFilter("");
    setMinBudget("");
    setMaxBudget("");
    setSearch("");
  };

  const sorted = useMemo(() => {
    const accessor = SORT_ACCESSORS[sortKey];
    if (!accessor) return filtered;
    const dir = sortDir === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const av = accessor(a);
      const bv = accessor(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [filtered, sortKey, sortDir]);

  return (
    <div className="cportal-page">
      <div className="cportal-page-head">
        <h1>{heading}</h1>
        {mode === "leads" && (
          <button
            type="button"
            className="cportal-btn cportal-btn-primary"
            onClick={() => navigate("/portal/leads/new")}
          >
            + Register Lead
          </button>
        )}
      </div>

      {error && <div className="cportal-alert cportal-alert-error">{error}</div>}

      {mode !== "clients" && (
      <div className="cportal-filters">
        <div className="cportal-filter-presets">
          {DATE_PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              className={`cportal-chip ${datePreset === p.id ? "active" : ""}`}
              onClick={() => setDatePreset(p.id)}
            >
              {p.label}
            </button>
          ))}
        </div>

        {datePreset === "custom" && (
          <div className="cportal-filter-dates">
            <label className="cportal-field">
              <span>From date</span>
              <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
            </label>
            <label className="cportal-field">
              <span>To date</span>
              <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
            </label>
          </div>
        )}

        <div className="cportal-filter-row">
          <input
            className="cportal-search cportal-filter-search"
            type="text"
            placeholder="Search name, phone, email"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All Status</option>
            {statusOptions.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}>
            <option value="">All Sources</option>
            {sourceOptions.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <select value={locationFilter} onChange={(e) => setLocationFilter(e.target.value)}>
            <option value="">All Locations</option>
            {locationOptions.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <input
            className="cportal-budget"
            type="number"
            placeholder="Min Budget"
            value={minBudget}
            onChange={(e) => setMinBudget(e.target.value)}
          />
          <input
            className="cportal-budget"
            type="number"
            placeholder="Max Budget"
            value={maxBudget}
            onChange={(e) => setMaxBudget(e.target.value)}
          />
          {hasActiveFilters && (
            <button type="button" className="cportal-clear-filters" onClick={clearFilters}>
              Clear
            </button>
          )}
        </div>
      </div>
      )}

      <div className="cportal-card">
        <div className="cportal-card-head">
          <span className="cportal-muted">{sorted.length} {noun}(s)</span>
        </div>

        {loading ? (
          <p className="cportal-muted">Loading…</p>
        ) : sorted.length === 0 ? (
          <p className="cportal-muted">No {noun}s found.</p>
        ) : (
          <div className="cportal-table-wrap">
            <table className="cportal-table">
              <thead>
                <tr>
                  {COLUMNS.map((col) => (
                    <th key={col.key} className="cportal-th-sortable">
                      <SupportSortableHeader
                        columnId={col.key}
                        label={col.label}
                        sortColumn={sortKey}
                        sortDirection={sortDir}
                        onSort={toggleSort}
                      />
                    </th>
                  ))}
                  <th className="cportal-th-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((l) => (
                  <tr key={l.Real_Estate_Lead_Id}>
                    <td>
                      <button
                        type="button"
                        className="cportal-table-link"
                        onClick={() =>
                          navigate(`/portal/leads/${l.Real_Estate_Lead_Id}`, { state: { from: mode } })
                        }
                      >
                        {fullName(l)}
                      </button>
                    </td>
                    <td>
                      <div className="cportal-contact-cell">
                        <span>{phone(l)}</span>
                        {l.Mobile_Number && (
                          <button
                            type="button"
                            className="cportal-contact-qr-btn"
                            onClick={() => openMobileQr(l)}
                            title="Show dial QR"
                            aria-label="Show dial QR code"
                          >
                            <FaQrcode size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                    <td>{l.Email || "—"}</td>
                    <td>{l.Property_Service || "—"}</td>
                    <td>{l.Tenant_Project_Name || "—"}</td>
                    <td>{l.Requirement_Type || "—"}</td>
                    <td>
                      <span className="cportal-badge">{l.Lead_Status || "—"}</span>
                    </td>
                    <td>{formatDate(l.Created_On)}</td>
                    <td>
                      <div className="cportal-re-actions">
                        <button
                          type="button"
                          className="re-lead-action re-lead-action--edit"
                          onClick={() =>
                            navigate(`/portal/leads/${l.Real_Estate_Lead_Id}`, {
                              state: { edit: true, from: mode },
                            })
                          }
                          title="Edit lead"
                          aria-label="Edit lead"
                        >
                          <FaEdit size={16} />
                        </button>
                        {l.Mobile_Number && (
                          <>
                            <a
                              className="re-lead-action re-lead-action--whatsapp"
                              href={`https://wa.me/${String(l.Country_Code || "").replace(/\D/g, "")}${String(l.Mobile_Number).replace(/\D/g, "")}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="WhatsApp"
                              aria-label="Open WhatsApp"
                            >
                              <FaWhatsapp size={14} />
                            </a>
                            <a
                              className="re-lead-action re-lead-action--call"
                              href={`tel:${normalizedCountryCode(l)}${l.Mobile_Number}`}
                              title="Call"
                              aria-label="Call lead"
                            >
                              <FaPhone size={12} style={{ transform: "scaleX(-1)" }} />
                            </a>
                          </>
                        )}
                        {l.Email && (
                          <a
                            className="re-lead-action re-lead-action--email"
                            href={`mailto:${l.Email}`}
                            title="Email"
                            aria-label="Email lead"
                          >
                            <FaEnvelope size={12} />
                          </a>
                        )}
                        <button
                          type="button"
                          className="re-lead-action re-lead-action--share"
                          onClick={() => openShare(l)}
                          title="Share"
                          aria-label="Share lead"
                        >
                          <FaShareAlt size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {shareModal.open && (
        <div className="cportal-modal-overlay" onClick={closeShare}>
          <div className="cportal-modal lead-share-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cportal-modal-header">
              <h3>Share lead</h3>
              <button type="button" onClick={closeShare}>
                Close
              </button>
            </div>
            <div className="lead-share-modal-body">
              <p className="lead-share-title" title={shareModal.title}>
                {shareModal.title}
              </p>
              <div className="lead-share-preview">{shareModal.text}</div>
              <div className="lead-share-options">
                <a
                  className="lead-share-option whatsapp"
                  href={`https://wa.me/?text=${encodeURIComponent(shareModal.text)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={closeShare}
                >
                  <FaWhatsapp /> WhatsApp
                </a>
                <a
                  className="lead-share-option email"
                  href={`mailto:?subject=${encodeURIComponent(shareModal.title)}&body=${encodeURIComponent(shareModal.text)}`}
                  onClick={closeShare}
                >
                  <FaEnvelope /> Email
                </a>
                <button type="button" className="lead-share-option copy" onClick={copyShareText}>
                  <FaRegCopy /> Copy details
                </button>
                {typeof navigator !== "undefined" && navigator.share && (
                  <button type="button" className="lead-share-option more" onClick={nativeShare}>
                    <FaShareAlt /> More apps
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <MobileDialQrModal
        open={qrModal.open}
        title={qrModal.title}
        number={qrModal.number}
        dataUrl={qrModal.dataUrl}
        onClose={closeMobileQr}
      />
    </div>
  );
}

export default CustomerLeads;
